import React, { useContext, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Button, Form, FormGroup, Input, Label } from 'reactstrap'

import { API } from 'API'
import { UserAccessPermissionsEnum } from 'constants/userAccessPermissionsEnum'
import { RequestToasterContext } from 'containers/Providers'
import { getUserAuthorities } from 'core/auth'
import {
  UserAccessPermissionsST,
  UserAccessPermissionsCMS,
  UserAccessPermissionsDTX,
  UserAccessPermissionsPHI,
  UserAccessPermissions,
  ROLE_BASED_PERMISSIONS,
  UserRoles,
} from 'domains/user'

interface AccessPermissionsControlProps {
  username: string
  role: '' | UserRoles
  permissions: string
  onUpdated?: () => void
}

const ST_PERMISSIONS = Object.values(UserAccessPermissionsST)
const CMS_PERMISSIONS = Object.values(UserAccessPermissionsCMS)
const DTX_PERMISSIONS = Object.values(UserAccessPermissionsDTX)
const ALL_PERMISSIONS = Object.values(UserAccessPermissions)
const ALL_PHI_PERMISSIONS = Object.values(UserAccessPermissionsPHI)

const PHI_PERMISSION_OPTIONS = [
  { label: 'None', value: '' },
  ...Object.entries(UserAccessPermissionsPHI).map(([key, value]) => ({
    label: key
      .replace('DTX_PHI_DATA_ACCESS_', '')
      .replaceAll('_', ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase()),
    value,
  })),
]

const PHI_HIERARCHY: Record<string, string[]> = {
  [UserAccessPermissionsPHI.DTX_PHI_DATA_ACCESS_WRITE]: ALL_PHI_PERMISSIONS,
  [UserAccessPermissionsPHI.DTX_PHI_DATA_ACCESS_READ_ONLY]: [
    UserAccessPermissionsPHI.DTX_PHI_DATA_ACCESS_READ_ONLY,
    UserAccessPermissionsPHI.DTX_PHI_DATA_ACCESS_READ_NAMES_ONLY,
  ],
  [UserAccessPermissionsPHI.DTX_PHI_DATA_ACCESS_READ_NAMES_ONLY]: [
    UserAccessPermissionsPHI.DTX_PHI_DATA_ACCESS_READ_NAMES_ONLY,
  ],
}

const getExpandedPhiPermissions = (selected: string): string[] => PHI_HIERARCHY[selected] ?? []

const derivePhiPermission = (userPermissions: string[]): string => {
  const set = new Set(userPermissions)

  const hasWrite = set.has(UserAccessPermissionsPHI.DTX_PHI_DATA_ACCESS_WRITE)
  const hasReadOnly = set.has(UserAccessPermissionsPHI.DTX_PHI_DATA_ACCESS_READ_ONLY)
  const hasReadNamesOnly = set.has(UserAccessPermissionsPHI.DTX_PHI_DATA_ACCESS_READ_NAMES_ONLY)

  if (hasWrite && hasReadOnly && hasReadNamesOnly) {
    return UserAccessPermissionsPHI.DTX_PHI_DATA_ACCESS_WRITE
  }

  if (hasWrite) {
    return UserAccessPermissionsPHI.DTX_PHI_DATA_ACCESS_WRITE
  }

  if (hasReadOnly && hasReadNamesOnly) {
    return UserAccessPermissionsPHI.DTX_PHI_DATA_ACCESS_READ_ONLY
  }

  if (hasReadNamesOnly) {
    return UserAccessPermissionsPHI.DTX_PHI_DATA_ACCESS_READ_NAMES_ONLY
  }

  return ''
}

export const AccessPermissionsControl = ({
  username,
  role,
  permissions,
  onUpdated,
}: AccessPermissionsControlProps) => {
  const form = useForm<{ permissions: Record<string, boolean> }>({
    mode: 'onSubmit',
  })
  const { requestStatusRef } = useContext(RequestToasterContext)

  const userPermissions = useMemo(
    () => (permissions ? permissions.split(',').map((perm) => perm.trim()) : []),
    [permissions],
  )

  const editablePermissions = useMemo(() => ROLE_BASED_PERMISSIONS[role] || [], [role])
  const [isEditPermissionAllowed, setIsEditPermissionAllowed] = useState<boolean | undefined>(false)

  const [selectedPhiPermission, setSelectedPhiPermission] = useState<string>('')

  const initialValues = useMemo(() => {
    return ALL_PERMISSIONS.reduce(
      (acc, permission) => {
        acc[permission] = userPermissions.includes(permission)
        return acc
      },
      {} as Record<string, boolean>,
    )
  }, [userPermissions])

  useEffect(() => {
    setIsEditPermissionAllowed(
      getUserAuthorities().includes(UserAccessPermissionsEnum.SUPPORT_TOOL_EDIT_PERMISSIONS),
    )
  }, [])

  useEffect(() => {
    form.reset({ permissions: initialValues })
  }, [initialValues, form])

  useEffect(() => {
    setSelectedPhiPermission(derivePhiPermission(userPermissions))
  }, [userPermissions])

  const updatePermissions = (data: { permissions: Record<string, boolean> }) => {
    const selectedPermissions = Object.entries(data.permissions)
      .filter(([, isSelected]) => isSelected)
      .map(([permission]) => permission as UserAccessPermissions)

    API.updateAccessPermissions(username, selectedPermissions.join(','))
      .then((status) => {
        if (status === 200) {
          requestStatusRef.current?.showAlert('Updated', 'success')
          onUpdated?.()
        }
      })
      .catch(() => {
        requestStatusRef.current?.showAlert('Failed to update permissions', 'danger')
      })
  }

  const handleSelectAll = () => {
    const selected = Object.values(form.getValues('permissions')).filter((isSelected) => isSelected)
    const isSelected = selected.length !== ALL_PERMISSIONS.length

    ALL_PERMISSIONS.forEach((permission) => {
      form.setValue(`permissions.${permission}`, isSelected)
    })
  }

  const isButtonDisabled = editablePermissions.length === 0 || !isEditPermissionAllowed

  const isPhiDropdownEnabledForRole = (userRole: UserRoles | ''): boolean => {
    return userRole === 'ROLE_USER_ADMIN'
  }

  return (
    <FormProvider {...form}>
      <Form onSubmit={form.handleSubmit(updatePermissions)}>
        <div className="d-flex justify-content-end mb-2">
          <Button type="button" color="link" size="sm" onClick={handleSelectAll}>
            Select all
          </Button>
        </div>
        <div className="mb-2">SUPPORT TOOL</div>
        <div
          className="p-2 d-flex flex-column"
          style={{
            border: 'solid #dee2e6 1px',
            borderRadius: 4,
            maxHeight: 180,
            overflow: 'auto',
            gap: '0.5rem',
          }}>
          {ST_PERMISSIONS.map((permission) => (
            <div key={permission} className="form-check">
              <input
                type="checkbox"
                id={permission}
                className="form-check-input"
                disabled={!editablePermissions.includes(permission) || !isEditPermissionAllowed}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...form.register(`permissions.${permission}`)}
              />
              <label className="form-check-label" htmlFor={permission}>
                {permission}
              </label>
            </div>
          ))}
        </div>

        <div className="mt-4 mb-2">CMS</div>
        <div
          className="p-2 d-flex flex-column"
          style={{
            border: 'solid #dee2e6 1px',
            borderRadius: 4,
            maxHeight: 180,
            overflow: 'auto',
            gap: '0.5rem',
          }}>
          {CMS_PERMISSIONS.map((permission) => (
            <div key={permission} className="form-check">
              <input
                type="checkbox"
                id={permission}
                className="form-check-input"
                disabled={!editablePermissions.includes(permission) || !isEditPermissionAllowed}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...form.register(`permissions.${permission}`)}
              />
              <label className="form-check-label" htmlFor={permission}>
                {permission}
              </label>
            </div>
          ))}
        </div>

        <div className="mt-4 mb-2">DTX</div>
        <div
          className="p-2 d-flex flex-column"
          style={{
            border: 'solid #dee2e6 1px',
            borderRadius: 4,
            maxHeight: 180,
            overflow: 'auto',
            gap: '0.5rem',
          }}>
          {DTX_PERMISSIONS.map((permission) => (
            <div key={permission} className="form-check">
              <input
                type="checkbox"
                id={permission}
                className="form-check-input"
                disabled={!editablePermissions.includes(permission) || !isEditPermissionAllowed}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...form.register(`permissions.${permission}`)}
              />
              <label className="form-check-label" htmlFor={permission}>
                {permission}
              </label>
            </div>
          ))}
        </div>

        <FormGroup className="mt-4 mb-3">
          <Label for="phiPermissionDropdown" className="mr-2">
            PHI Data Access:
          </Label>
          <Input
            type="select"
            id="phiPermissionDropdown"
            value={selectedPhiPermission}
            disabled={!isPhiDropdownEnabledForRole(role)}
            onChange={(e) => {
              const selected = e.target.value

              setSelectedPhiPermission(selected)

              const newValues = { ...form.getValues('permissions') }

              ALL_PHI_PERMISSIONS.forEach((perm) => {
                newValues[perm] = false
              })
              getExpandedPhiPermissions(selected).forEach((perm) => {
                newValues[perm] = true
              })
              form.setValue('permissions', newValues, { shouldDirty: true, shouldValidate: true })
            }}>
            {PHI_PERMISSION_OPTIONS.map(({ label, value }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Input>
        </FormGroup>

        <div className="d-flex justify-content-end mt-3" style={{ gap: '1rem' }}>
          <button type="submit" disabled={isButtonDisabled}>
            Update Access Permissions
          </button>
        </div>
      </Form>
    </FormProvider>
  )
}
